import { useEffect, useState } from "react";
import { adminApi, type AdminUser } from "@/lib/admin";
import { useAuth } from "@/lib/auth";

export function AdminUsers() {
  const auth = useAuth();
  const myKickId = auth.loading ? "" : auth.user?.kickUserId ?? "";
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);

  async function refresh() {
    setErr(null);
    try {
      const data = await adminApi.users();
      setUsers(data);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function toggleRole(u: AdminUser) {
    if (u.kickUserId === myKickId) {
      alert("Nie możesz zmienić własnej roli — admin musi mieć drugiego.");
      return;
    }
    const next = u.role === "Admin" ? "Visitor" : "Admin";
    if (!confirm(`Zmienić rolę @${u.kickUsername} na ${next}?`)) return;
    setWorking(u.id);
    try {
      await adminApi.setUserRole(u.id, next);
      await refresh();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setWorking(null);
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex items-baseline justify-between">
        <h1 className="font-display font-bold text-3xl">Użytkownicy</h1>
        {users && (
          <p className="text-sm font-mono text-carp-ink/60">
            {users.length} kont
          </p>
        )}
      </header>

      {err && (
        <div className="brut bg-carp-destructive text-white p-3 text-sm">{err}</div>
      )}

      <div className="brut bg-carp-paper overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-carp-ink text-carp-paper text-left font-display uppercase text-xs">
            <tr>
              <th className="p-2">Kick</th>
              <th className="p-2">E-mail</th>
              <th className="p-2">Rola</th>
              <th className="p-2 text-right">Logowań</th>
              <th className="p-2">Ostatnie</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-carp-ink/10">
            {users?.map((u) => (
              <tr key={u.id} className={u.kickUserId === myKickId ? "bg-carp-yellow/40" : ""}>
                <td className="p-2 font-bold">@{u.kickUsername}</td>
                <td className="p-2 text-xs">{u.email ?? "—"}</td>
                <td className="p-2">
                  <span
                    className={`chip ${u.role === "Admin" ? "bg-carp-cyan" : "bg-white"}`}
                  >
                    {u.role}
                  </span>
                </td>
                <td className="p-2 text-right font-mono">{u.loginCount}</td>
                <td className="p-2 font-mono text-[11px] text-carp-ink/70 whitespace-nowrap">
                  {new Date(u.lastLoginAt).toLocaleString("pl-PL")}
                </td>
                <td className="p-2 text-right">
                  {u.kickUserId === myKickId ? (
                    <span className="text-xs text-carp-ink/50 italic">to ty</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleRole(u)}
                      disabled={working === u.id}
                      className="btn-ghost text-xs disabled:opacity-50"
                    >
                      {u.role === "Admin" ? "→ Visitor" : "→ Admin"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users && users.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-carp-ink/50">
                  Jeszcze nikt się nie zalogował
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-carp-ink/60">
        Adminowie wymienieni w <code className="font-mono bg-carp-ink/10 px-1">Admins:KickUsernames</code>{" "}
        dostają rolę Admin automatycznie przy każdym logowaniu (nie da się ich na stałe
        zdemotować przez ten panel).
      </p>
    </div>
  );
}
